/**
 * OrbitEngine — single source of truth for orbit-camera math.
 *
 * No dependency on Viewer / Scene / viewerMode / InputHandler. Adapters
 * (OrbitControls for the main view, WidgetOrbitController for widgets)
 * own input event sources, mode flags, picking, and side effects; this
 * module owns the math.
 *
 * The math is lifted byte-equivalent from OrbitControls.update so the main
 * view's behavior is unchanged. Asymmetric radius decay (linear, not
 * exponential) is preserved deliberately — see `applyDeltas`.
 */

import * as THREE from 'three';
import type { View } from '../../viewer/View';

declare const TWEEN: {
  Easing: { Quartic: { Out: unknown } };
  Tween: new (obj: object) => TweenInstance;
};

export interface TweenInstance {
  to(target: object, duration: number): TweenInstance;
  easing(fn: unknown): TweenInstance;
  onUpdate(callback: () => void): TweenInstance;
  onComplete(callback: () => void): TweenInstance;
  start(): TweenInstance;
  stop(): void;
}

/** Per-tick input deltas. Caller owns one of these and resets/inspects as needed. */
export interface OrbitDeltas {
  yawDelta: number;
  pitchDelta: number;
  rollDelta: number;
  panDelta: THREE.Vector2;
  /** Linear delta on radius. Decayed with `radiusDelta -= progression * radiusDelta` per tick. */
  radiusDelta: number;
}

export function createDeltas(): OrbitDeltas {
  return {
    yawDelta: 0,
    pitchDelta: 0,
    rollDelta: 0,
    panDelta: new THREE.Vector2(0, 0),
    radiusDelta: 0,
  };
}

export function resetDeltas(d: OrbitDeltas): void {
  d.yawDelta = 0;
  d.pitchDelta = 0;
  d.rollDelta = 0;
  d.panDelta.set(0, 0);
  d.radiusDelta = 0;
}

/** Tunables. Defaults match the main view's OrbitControls today. */
export interface OrbitTuning {
  fadeFactor: number;
  rotationSpeed: number;
}

export const DEFAULT_TUNING: OrbitTuning = {
  fadeFactor: 20,
  rotationSpeed: 5,
};

export type OrbitCamera = THREE.PerspectiveCamera | THREE.OrthographicCamera;

export interface ApplyDeltasOptions {
  /** Whether yaw rotation is allowed (false in 2D mode). */
  allowYaw: boolean;
  /** Whether pitch rotation is allowed (false in 2D mode). */
  allowPitch: boolean;
}

// ===== Input → delta translators =====================================
// Adapters call these instead of accumulating into delta fields directly.
// Keeps every "this is how a wheel/drag affects the orbit" decision in one
// place so the widget gets the same math the main view has.

/**
 * Pan from a drag in normalized canvas coords (dx/canvasW, dy/canvasH).
 * Accumulates into `panDelta`; per-tick decay handles smoothing.
 */
export function applyDragPan(deltas: OrbitDeltas, ndx: number, ndy: number): void {
  deltas.panDelta.x += ndx;
  deltas.panDelta.y += ndy;
}

/**
 * Orbit (yaw + pitch) from a drag in normalized canvas coords.
 * `rotationSpeed` defaults to {@link DEFAULT_TUNING}.rotationSpeed.
 */
export function applyDragOrbit(deltas: OrbitDeltas, ndx: number, ndy: number, rotationSpeed: number = DEFAULT_TUNING.rotationSpeed): void {
  deltas.yawDelta += ndx * rotationSpeed;
  deltas.pitchDelta += ndy * rotationSpeed;
}

/**
 * 3D wheel zoom: nudge `radiusDelta` proportional to the current resolved
 * radius (so the same wheel motion zooms by the same fraction at any
 * distance). `wheelDelta` is the normalized scroll direction (positive =
 * zoom out, matching the main view's existing convention).
 */
export function applyWheelZoom3D(deltas: OrbitDeltas, view: View, wheelDelta: number): void {
  const resolvedRadius = view.radius + deltas.radiusDelta;
  deltas.radiusDelta += -wheelDelta * resolvedRadius * 0.1;
}

/**
 * 2D wheel pan. `wheelDelta` is the vertical scroll, `deltaX` the
 * horizontal scroll (some mice/trackpads emit either or both — a diagonal
 * two-finger trackpad scroll emits both at once, so the axes apply
 * TOGETHER, not either/or). `shift` swaps axes (matches main view's
 * behavior); a discrete mouse wheel only ever emits the vertical delta.
 */
export function applyWheelPan2D(deltas: OrbitDeltas, wheelDelta: number, deltaX: number, shift: boolean, panSpeed = 0.015): void {
  if (shift) {
    deltas.panDelta.y += deltaX * panSpeed;
    deltas.panDelta.x += -wheelDelta * panSpeed;
  } else {
    deltas.panDelta.x += -deltaX * panSpeed;
    deltas.panDelta.y += wheelDelta * panSpeed;
  }
}

/**
 * Per-tick integrator. Reads accumulated deltas, applies smoothing, writes
 * the new pose into `view`. Decay is asymmetric on purpose:
 * - yaw/pitch/roll/pan: exponential (`*= attenuation`)
 * - radius:             linear (`-= progression * radiusDelta`)
 * This matches the original OrbitControls.update math line-for-line.
 *
 * `view.direction` is read twice (once in the rotation block, once in the
 * zoom block). The second read intentionally sees the post-rotation
 * direction; do not hoist into a single call.
 */
export function applyDeltas(view: View, deltas: OrbitDeltas, camera: OrbitCamera, dt: number, opts: ApplyDeltasOptions, tuning: OrbitTuning = DEFAULT_TUNING): void {
  const { allowYaw, allowPitch } = opts;
  const fadeFactor = tuning.fadeFactor;

  // --- rotation around the captured pivot ---
  {
    const progression = Math.min(1, fadeFactor * dt);

    let yaw = view.yaw;
    let pitch = view.pitch;
    const pivot = view.getPivot();

    if (allowYaw) yaw -= progression * deltas.yawDelta;
    if (allowPitch) pitch -= progression * deltas.pitchDelta;

    if (allowYaw) view.yaw = yaw;
    if (allowPitch) view.pitch = pitch;

    const V = view.direction.multiplyScalar(-view.radius);
    const position = new THREE.Vector3().addVectors(pivot, V);
    view.position.copy(position);
  }

  // --- pan in screen-aligned plane ---
  {
    const progression = Math.min(1, fadeFactor * dt);
    const distance = view.radius;

    let targetWidth: number, targetHeight: number;
    if (camera instanceof THREE.PerspectiveCamera) {
      const fov = (camera.fov * Math.PI) / 180;
      targetHeight = 2 * Math.tan(fov / 2) * distance;
      targetWidth = targetHeight * camera.aspect;
    } else {
      // Ortho: pan distance scales with the visible frustum.
      targetWidth = (camera.right * 2) / camera.zoom;
      targetHeight = (camera.top * 2) / camera.zoom;
    }

    const px = -deltas.panDelta.x * progression * targetWidth;
    const py = deltas.panDelta.y * progression * targetHeight;
    view.pan(px, py);
  }

  // --- zoom: nudge radius ---
  // Perspective: also reposition the camera along (-direction * radius) from
  // the pivot. Moving the camera IS what zoom feels like in 3D, and the
  // pipeline's near/far gets retuned each frame against cloud bounds, so
  // dollying past geometry isn't a problem.
  // Orthographic: keep camera position locked. Radius drives the frustum
  // height (`top = view.radius` in adapters' applyToCamera), so zoom is
  // purely a projection change. Two reasons we lock position:
  //   1. Tools (e.g. wall-focus) tighten the camera near/far around a slab;
  //      a zoom that moves the camera would push it through that slab,
  //      either clipping the wall away or escaping into other geometry.
  //   2. Ortho is conceptually a 2D pan/zoom over a fixed scene plane —
  //      "zooming past objects" by moving the camera has no visual benefit.
  // Mouse-anchor stability is preserved by zoomTowardsMouse's compensating
  // panDelta, which scales with the post-zoom frustum size.
  {
    const progression = Math.min(1, fadeFactor * dt);
    // Clamp the radius to a small positive minimum so an aggressive sequence
    // of zoom-in inputs can't drive radius through zero. Negative radius
    // inverts the orthographic frustum (top/bottom swap signs) — manifests
    // as a "camera flip" when the user zooms past the pivot. Also degenerate
    // for perspective: lookAt(pivot) becomes ill-defined when the camera
    // position coincides with the pivot.
    const MIN_RADIUS = 1e-3;
    const radius = Math.max(MIN_RADIUS, view.radius + progression * deltas.radiusDelta);

    const isOrtho = (camera as THREE.OrthographicCamera).isOrthographicCamera === true;
    if (isOrtho) {
      view.radius = radius;
    } else {
      const V = view.direction.multiplyScalar(-radius);
      const position = new THREE.Vector3().addVectors(view.getPivot(), V);
      view.radius = radius;
      view.position.copy(position);
    }
  }

  // --- decay deltas ---
  {
    const progression = Math.min(1, fadeFactor * dt);
    const attenuation = Math.max(0, 1 - fadeFactor * dt);

    deltas.yawDelta *= attenuation;
    if (allowPitch) deltas.pitchDelta *= attenuation;
    deltas.rollDelta *= attenuation;
    deltas.panDelta.multiplyScalar(attenuation);
    // Linear decay for radius (the comment in the original code preserved):
    // // deltas.radiusDelta *= attenuation;
    deltas.radiusDelta -= progression * deltas.radiusDelta;
  }
}

/**
 * Zoom toward the cursor: nudge radius and add a compensating pan so the
 * world point under the cursor stays under the cursor.
 *
 * Pure: only mutates `deltas.radiusDelta` and `deltas.panDelta`. Caller is
 * responsible for `stopTweens` (the main view's behavior) if any tween was
 * in flight before this is called.
 *
 * `mouse` is in canvas-pixel coordinates (origin top-left). canvasW/H are
 * the canvas client dimensions.
 */
export function zoomTowardsMouse(view: View, camera: OrbitCamera, mouse: THREE.Vector2, canvasW: number, canvasH: number, factor: number, deltas: OrbitDeltas): void {
  // Mouse in normalized centered coords (-0.5 to 0.5, +Y up).
  const mouseNormX = mouse.x / canvasW - 0.5;
  const mouseNormY = -(mouse.y / canvasH - 0.5);

  // World-space size of the visible frustum at the orbit's current distance.
  const distance = view.radius;
  let targetWidth: number, targetHeight: number;
  if (camera instanceof THREE.PerspectiveCamera) {
    const fov = (camera.fov * Math.PI) / 180;
    targetHeight = 2 * Math.tan(fov / 2) * distance;
    targetWidth = targetHeight * camera.aspect;
  } else {
    targetWidth = (camera.right * 2) / camera.zoom;
    targetHeight = (camera.top * 2) / camera.zoom;
  }

  // Mouse offset from frustum center in world space, before zoom.
  const mouseOffsetX = mouseNormX * targetWidth;
  const mouseOffsetY = mouseNormY * targetHeight;

  // Apply zoom to radiusDelta. resolvedRadius accounts for prior in-flight zoom.
  const resolvedRadius = view.radius + deltas.radiusDelta;
  const newRadius = resolvedRadius * (1 + factor);
  deltas.radiusDelta = newRadius - view.radius;

  // World-space frustum size after the zoom completes.
  const newDistance = newRadius;
  let newTargetWidth: number, newTargetHeight: number;
  if (camera instanceof THREE.PerspectiveCamera) {
    const fov = (camera.fov * Math.PI) / 180;
    newTargetHeight = 2 * Math.tan(fov / 2) * newDistance;
    newTargetWidth = newTargetHeight * camera.aspect;
  } else {
    const zoomRatio = newRadius / resolvedRadius;
    newTargetWidth = targetWidth * zoomRatio;
    newTargetHeight = targetHeight * zoomRatio;
  }

  // Mouse offset from center after zoom (same screen position).
  const newMouseOffsetX = mouseNormX * newTargetWidth;
  const newMouseOffsetY = mouseNormY * newTargetHeight;

  // Compensating pan: keep the cursor pointing at the same world point.
  const panOffsetX = mouseOffsetX - newMouseOffsetX;
  const panOffsetY = mouseOffsetY - newMouseOffsetY;
  deltas.panDelta.x -= panOffsetX / targetWidth;
  deltas.panDelta.y += panOffsetY / targetHeight;
}

export interface ZoomToLocationParams {
  /** The View whose position/radius will be tweened. */
  view: View;
  /** World-space target position the camera will move to. */
  targetPosition: THREE.Vector3;
  /** Final radius after the tween completes. */
  targetRadius: number;
  /** Tween duration in milliseconds. Defaults to 600. */
  durationMs?: number;
  /** Per-tick callback. Main view uses this for `viewer.setMoveSpeed(view.radius)`; widgets use it to push the new pose onto their camera. */
  onTick?: (view: View) => void;
  /** Shared tween list. Caller is responsible for `stopTweens(list)` when a new gesture starts or a view-switch happens. */
  tweens: TweenInstance[];
}

/**
 * Tween view.position and view.radius linearly toward the target, with
 * Quartic.Out easing applied to the parameter `t` via TWEEN.Easing.Quartic.Out.
 * The interpolation itself is linear in `t`; the easing controls how `t`
 * evolves over time. This matches the main view's implementation exactly.
 */
export function zoomToLocation(params: ZoomToLocationParams): TweenInstance {
  const duration = params.durationMs ?? 600;
  const easing = TWEEN.Easing.Quartic.Out;

  const value = { x: 0 };
  const tween = new TWEEN.Tween(value).to({ x: 1 }, duration);
  tween.easing(easing);
  params.tweens.push(tween);

  const startPos = params.view.position.clone();
  const targetPos = params.targetPosition.clone();
  const startRadius = params.view.radius;
  const targetRadius = params.targetRadius;

  tween.onUpdate(() => {
    const t = value.x;
    params.view.position.x = (1 - t) * startPos.x + t * targetPos.x;
    params.view.position.y = (1 - t) * startPos.y + t * targetPos.y;
    params.view.position.z = (1 - t) * startPos.z + t * targetPos.z;
    params.view.radius = (1 - t) * startRadius + t * targetRadius;
    params.onTick?.(params.view);
  });

  tween.onComplete(() => {
    const idx = params.tweens.indexOf(tween);
    if (idx !== -1) params.tweens.splice(idx, 1);
  });

  tween.start();
  return tween;
}

/** Stop and clear all tweens in the list. Safe to call when the list is empty. */
export function stopTweens(tweens: TweenInstance[]): void {
  for (const t of tweens) t.stop();
  tweens.length = 0;
}
